import { useTranslation } from "react-i18next";
import { Label } from "../../../../helpers/historyHelpers/components/Label";
import { firstToUpper } from "../../../../../../utils/helpers/string";
import { OldValue } from "../../../../helpers/historyHelpers/components/OldValue";
import { NewValue } from "../../../../helpers/historyHelpers/components/NewValue";
import { renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";
import { useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import moment from "moment-timezone";

const fields = [
    "accural",
    "carryover",
    "description",
    "minRequestedHours",
    "maxBalance",
    "minBalance",
    "payCode",
    "prohibition",
    "reset",
    "status",
    "tierBy",
    "tiers",
];

const accuralFields = ["earningGroups", "intervals", "payCodes", "groupings", "type", "method"];
const prohibitionFields = ["allowAccural", "payCode", "value", "allowRequests"];
const resetFields = ["type", "value"];

const RenderItem = ({ data }) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();

    const renderValues = (item, data) => {
        const newValue = data?.[item]?.new;
        const oldValue = data?.[item]?.old;
        if (!data) return null;
        switch (item) {
            case "minBalance":
            case "minRequestedHours":
            case "description":
            case "status":
                return (
                    <div className="d-flex align-items-center">
                        {oldValue && <OldValue value={t(oldValue)} />}
                        {newValue && <NewValue value={t(newValue)} />}
                    </div>
                );
            case "payCode":
                return (
                    <div className="d-flex align-items-center">
                        {oldValue && <OldValue value={renderSettingsResourceLabel(oldValue)} />}
                        {newValue && <NewValue value={renderSettingsResourceLabel(newValue)} />}
                    </div>
                );
            case "tierBy":
                return (
                    <div>
                        {oldValue && <OldValue value={t(oldValue)} />}
                        {newValue && <NewValue value={t(newValue)} />}
                    </div>
                );
            case "accural":
                return (
                    <div className="d-flex flex-column gap-1">
                        {accuralFields.map((field) => {
                            const fieldNew = data.accural[field]?.new;
                            const fieldOld = data.accural[field]?.old;

                            if (fieldNew === undefined && fieldOld === undefined) return null;

                            switch (field) {
                                case "method":
                                    return (
                                        <div className="d-flex align-items-center">
                                            <span className="pr-1">{t("method")}: </span>
                                            {fieldOld && <OldValue value={t(fieldOld)} />}
                                            {fieldNew && <NewValue value={t(fieldNew)} />}
                                        </div>
                                    );
                                case "earningGroups":
                                    const formattedNewEarningGroups = (
                                        <div className="d-flex flex-column">
                                            {fieldNew?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        {renderSettingsResourceLabel(item)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    const formattedOldEarningGroups = (
                                        <div className="d-flex flex-column">
                                            {fieldOld?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        <div key={i} className="d-flex gap-2">
                                                            {renderSettingsResourceLabel(item)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    return (
                                        <div className="d-flex align-items-center">
                                            <span className="pr-1">{t("earning-group")}: </span>
                                            <OldValue value={formattedOldEarningGroups} />
                                            <NewValue value={formattedNewEarningGroups} />
                                        </div>
                                    );
                                case "groupings":
                                    const formattedNewGroupings = (
                                        <div className="d-flex flex-column">
                                            {fieldNew?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        {renderSettingsResourceLabel(item)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    const formattedOldGroupings = (
                                        <div className="d-flex flex-column">
                                            {fieldOld?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        <div key={i} className="d-flex gap-2">
                                                            {renderSettingsResourceLabel(item)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    return (
                                        <div className="d-flex align-items-center">
                                            <span className="pr-1">{t("groupings")}: </span>
                                            <OldValue value={formattedOldGroupings} />
                                            <NewValue value={formattedNewGroupings} />
                                        </div>
                                    );
                                case "intervals":
                                    const formattedNewIntervals = (
                                        <div className="d-flex flex-column">
                                            {fieldNew?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        {item?.max && (
                                                            <span>
                                                                {t("max")}: {item.max}
                                                            </span>
                                                        )}
                                                        {item?.value && (
                                                            <span>
                                                                {t("value")}: {item.value}
                                                            </span>
                                                        )}
                                                        {item?.earned && (
                                                            <span>
                                                                {t("earned")}: {item.earned}
                                                            </span>
                                                        )}
                                                        {item?.worked && (
                                                            <span>
                                                                {t("worked")}: {item.worked}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    const formattedOldIntervals = (
                                        <div className="d-flex flex-column">
                                            {fieldOld?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        {item?.max && (
                                                            <span>
                                                                {t("max")}: {item.max}
                                                            </span>
                                                        )}
                                                        {item?.value && (
                                                            <span>
                                                                {t("value")}: {item.value}
                                                            </span>
                                                        )}
                                                        {item?.earned && (
                                                            <span>
                                                                {t("earned")}: {item.earned}
                                                            </span>
                                                        )}
                                                        {item?.worked && (
                                                            <span>
                                                                {firstToUpper(t("worked"))}: {item.worked}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    return (
                                        <div className="d-flex align-items-center">
                                            <span className="pr-2">{t("intervals")}: </span>
                                            <OldValue value={formattedOldIntervals} />
                                            <NewValue value={formattedNewIntervals} />
                                        </div>
                                    );
                                case "payCodes":
                                    const formattedNewPayCodes = (
                                        <div className="d-flex flex-column">
                                            {fieldNew?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        {renderSettingsResourceLabel(item)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    const formattedOldPayCodes = (
                                        <div className="d-flex flex-column">
                                            {fieldOld?.map((item, i) => {
                                                return (
                                                    <div key={i} className="d-flex gap-2">
                                                        <div key={i} className="d-flex gap-2">
                                                            {renderSettingsResourceLabel(item)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );

                                    return (
                                        <div className="d-flex align-items-center">
                                            <span className="pr-1">{t("pay-code")}: </span>
                                            <OldValue value={formattedOldPayCodes} />
                                            <NewValue value={formattedNewPayCodes} />
                                        </div>
                                    );
                                case "type":
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pr-1">{t("type")}:</span>
                                            <OldValue value={t(fieldOld)} />
                                            <NewValue value={t(fieldNew)} />
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </div>
                );
            case "prohibition":
                return (
                    <div className="d-flex flex-column gap-1">
                        {prohibitionFields.map((field) => {
                            const fieldNew = data.prohibition[field]?.new;
                            const fieldOld = data.prohibition[field]?.old;

                            if (fieldNew === undefined && fieldOld === undefined) return null;

                            switch (field) {
                                case "payCode":
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pr-1">{t("pay-code")}:</span>
                                            <OldValue value={renderSettingsResourceLabel(fieldOld)} />
                                            <NewValue value={renderSettingsResourceLabel(fieldNew)} />
                                        </div>
                                    );
                                case "allowAccural":
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pr-1">{t("allow-accural")}:</span>
                                            {typeof fieldNew === "boolean" && (
                                                <OldValue value={fieldOld ? t("active") : t("inactive")} />
                                            )}
                                            {typeof fieldOld && (
                                                <NewValue value={fieldNew ? t("active") : t("inactive")} />
                                            )}
                                        </div>
                                    );
                                case "allowRequests":
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pr-1">{t("allow-accural")}:</span>
                                            {typeof fieldNew === "boolean" && (
                                                <OldValue value={fieldOld ? t("active") : t("inactive")} />
                                            )}
                                            {typeof fieldOld && (
                                                <NewValue value={fieldNew ? t("active") : t("inactive")} />
                                            )}
                                        </div>
                                    );
                                case "value":
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pr-1">{t("value")}:</span>
                                            <OldValue value={fieldOld} />
                                            <NewValue value={fieldNew} />
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </div>
                );
            case "reset":
                return (
                    <div>
                        {resetFields.map((field) => {
                            const fieldNew = data.accural[field]?.new;
                            const fieldOld = data.accural[field]?.old;

                            switch (field) {
                                case "type":
                                    return (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pr-1">{t("type")}:</span>
                                            <OldValue value={t(fieldOld)} />
                                            <NewValue value={t(fieldNew)} />
                                        </div>
                                    );
                                case "value":
                                    return (
                                        <div className="d-flex align-items-center gap-2 mt-2">
                                            <span className="pr-1">{t("date")}:</span>
                                            <OldValue value={moment(fieldOld).format(dateFormat)} />
                                            <NewValue value={moment(fieldNew).format(dateFormat)} />
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </div>
                );
            case "maxBalance":
            case "carryover":
                const carryoverNewValue = (
                    <div className="d-flex flex-column">
                        {data[item]?.new.map((item, i) => {
                            return (
                                <div key={i}>
                                    {item?.name && (
                                        <span>
                                            {t("name")}: {item.name}
                                        </span>
                                    )}
                                    {item?.start && (
                                        <span>
                                            {t("start")}: {item.start}
                                        </span>
                                    )}
                                    {item?.end && (
                                        <span>
                                            {t("end")}: {item.end}
                                        </span>
                                    )}
                                    {item?.value && (
                                        <span>
                                            {t("value")}: {item.value}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
                const carryOverOldValue = (
                    <div className="d-flex flex-column">
                        {data[item]?.old.map((item, i) => {
                            return (
                                <div key={i}>
                                    {item?.name && (
                                        <span>
                                            {t("name")}: {item.name}
                                        </span>
                                    )}
                                    {item?.start && (
                                        <span>
                                            {t("start")}: {item.start}
                                        </span>
                                    )}
                                    {item?.end && (
                                        <span>
                                            {t("end")}: {item.end}
                                        </span>
                                    )}
                                    {item?.value && (
                                        <span>
                                            {t("value")}: {item.value}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
                return (
                    <div className="d-flex align-items-center">
                        <OldValue value={carryOverOldValue} />
                        <NewValue value={carryoverNewValue} />
                    </div>
                );
            case "tiers":
                const tiersNewValue = (
                    <div className="d-flex flex-column">
                        {data[item]?.new.map((item, i) => (
                            <div key={i} className="d-flex gap-2">
                                {item?.name && (
                                    <span>
                                        {t("name")}: {item.name}
                                    </span>
                                )}
                                {item?.start && (
                                    <span>
                                        {t("start")}: {item.start}
                                    </span>
                                )}
                                {item?.end && (
                                    <span>
                                        {t("end")}: {item.end}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                );
                const tiersOldValue = (
                    <div className="d-flex flex-column">
                        {data[item]?.old.map((item, i) => (
                            <div key={i} className="d-flex gap-2">
                                {item?.name && (
                                    <span>
                                        {t("name")}: {item.name}
                                    </span>
                                )}
                                {item?.start && (
                                    <span>
                                        {t("start")}: {item.start}
                                    </span>
                                )}
                                {item?.end && (
                                    <span>
                                        {t("end")}: {item.end}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                );
                return (
                    <div className="d-flex align-items-center">
                        <OldValue value={tiersNewValue} />
                        <NewValue value={tiersOldValue} />
                    </div>
                );
            default:
                return null;
        }
    };
    return (
        <div className="tooltip-content-style text-left">
            {fields.map((item) => {
                if (!data[item]) return null;
                return (
                    <div key={item} className="py-1 d-flex flex-column gap-2">
                        <Label value={firstToUpper(t(item))} /> {renderValues(item, data)}
                    </div>
                );
            })}
        </div>
    );
};
export default RenderItem;
